import { getClientEventsServiceStatscFlushUrl } from "@/lib/clientEventsServiceConfig";

export type StatscTags = Readonly<Record<string, string>>;

type Counter = {
  namespace: string;
  metric: string;
  tags: StatscTags;
  value: number;
};

type Histogram = {
  namespace: string;
  metric: string;
  tags: StatscTags;
  values: number[];
};

type StatscFlushRequest = {
  counters: Counter[];
  histograms: Histogram[];
  client_type: "web";
};

class MetricsBuffer {
  readonly counters = new Map<string, Counter>();
  readonly histograms = new Map<string, Histogram>();
}

const POST_INTERVAL_MS = 30_000;
const STATSC_FLUSH_URL = getClientEventsServiceStatscFlushUrl(
  import.meta.env.CLIENT_EVENTS_SERVICE_DOMAIN,
  import.meta.env.VITE_ENV ?? "development",
);

class Statsc {
  private metrics = new MetricsBuffer();
  private scheduledFlush: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    if (!this.isEnabled()) {
      return;
    }

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "hidden") {
        void this.flushPendingMetrics(true);
      }
    });
    window.addEventListener("pagehide", () => {
      void this.flushPendingMetrics(true);
    });
  }

  count(namespace: string, metric: string, tags: StatscTags = {}, value: number = 1): void {
    if (!this.isEnabled()) {
      return;
    }

    const normalizedTags = { ...tags };
    const key = this.makeKey(namespace, metric, normalizedTags);
    const existingCounter = this.metrics.counters.get(key);
    if (existingCounter) {
      existingCounter.value += value;
    } else {
      this.metrics.counters.set(key, { namespace, metric, tags: normalizedTags, value });
    }
    this.scheduleFlush();
  }

  hist(namespace: string, metric: string, tags: StatscTags, value: number): void {
    if (!this.isEnabled()) {
      return;
    }

    const normalizedTags = { ...tags };
    const key = this.makeKey(namespace, metric, normalizedTags);
    const existingHistogram = this.metrics.histograms.get(key);
    if (existingHistogram) {
      existingHistogram.values.push(value);
    } else {
      this.metrics.histograms.set(key, {
        namespace,
        metric,
        tags: normalizedTags,
        values: [value],
      });
    }
    this.scheduleFlush();
  }

  async flush(): Promise<void> {
    await this.flushPendingMetrics(false);
  }

  private isEnabled(): boolean {
    return (
      STATSC_FLUSH_URL != null && typeof window !== "undefined" && typeof document !== "undefined"
    );
  }

  private makeKey(namespace: string, metric: string, tags: StatscTags): string {
    return JSON.stringify([
      namespace,
      metric,
      Object.entries(tags).sort(([left], [right]) => left.localeCompare(right)),
    ]);
  }

  private scheduleFlush(): void {
    if (this.scheduledFlush != null) {
      return;
    }
    this.scheduledFlush = setTimeout(() => {
      this.scheduledFlush = null;
      void this.flushPendingMetrics(false);
    }, POST_INTERVAL_MS);
  }

  private clearScheduledFlush(): void {
    if (this.scheduledFlush == null) {
      return;
    }
    clearTimeout(this.scheduledFlush);
    this.scheduledFlush = null;
  }

  private takePendingMetrics(): StatscFlushRequest | null {
    if (this.metrics.counters.size === 0 && this.metrics.histograms.size === 0) {
      return null;
    }

    const pendingMetrics = this.metrics;
    this.metrics = new MetricsBuffer();
    return {
      counters: [...pendingMetrics.counters.values()],
      histograms: [...pendingMetrics.histograms.values()],
      client_type: "web",
    };
  }

  private async flushPendingMetrics(useBeacon: boolean): Promise<void> {
    if (!this.isEnabled()) {
      return;
    }

    this.clearScheduledFlush();
    const pendingMetrics = this.takePendingMetrics();
    if (pendingMetrics == null) {
      return;
    }

    await this.sendMetrics(pendingMetrics, useBeacon);
    if (this.metrics.counters.size > 0 || this.metrics.histograms.size > 0) {
      this.scheduleFlush();
    }
  }

  private async sendMetrics(payload: StatscFlushRequest, useBeacon: boolean): Promise<void> {
    const body = JSON.stringify(payload);

    if (useBeacon && typeof navigator.sendBeacon === "function") {
      try {
        if (
          navigator.sendBeacon(STATSC_FLUSH_URL!, new Blob([body], { type: "application/json" }))
        ) {
          return;
        }
      } catch {
        // Fall through to keepalive fetch.
      }
    }

    try {
      await fetch(STATSC_FLUSH_URL!, {
        method: "POST",
        body,
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        keepalive: true,
      });
    } catch {
      // Operational metrics are best-effort and must never affect billing.
    }
  }
}

export const statsc = new Statsc();
